import React, { useEffect, useMemo, useState } from 'react';
import { BarChart3, CalendarClock, Mail, MessageSquare, Phone, Send, TrendingUp, Users } from 'lucide-react';
import api from '../lib/api';

interface UserOption {
  id: string;
  name: string;
  email: string;
  profile?: string;
}

interface Lead {
  id: string;
  name: string;
  phone: string;
  email?: string;
  interestType: 'COMPRA' | 'VENDA' | 'ALUGUEL';
  source: string;
  stage: 'NOVO_LEAD' | 'EM_CONTATO' | 'VISITA_AGENDADA' | 'PROPOSTA_ENVIADA' | 'NEGOCIACAO' | 'FECHADO' | 'PERDIDO';
  assignedBrokerId: string;
  assignedBroker?: { id: string; name: string; email: string };
  desiredRegion?: string;
  valueMin?: string | number;
  valueMax?: string | number;
  notes?: string;
  appointments?: Array<{ id: string; scheduledAt: string; location?: string }>;
}

interface DashboardData {
  totalLeads: number;
  conversionRate: number;
  capturedThisMonth: number;
  dealsClosedMonth: number;
  ranking: Array<{ brokerId: string; brokerName: string; closedDeals: number }>;
}

const stageLabels: Record<Lead['stage'], string> = {
  NOVO_LEAD: 'Novo lead',
  EM_CONTATO: 'Em contato',
  VISITA_AGENDADA: 'Visita agendada',
  PROPOSTA_ENVIADA: 'Proposta enviada',
  NEGOCIACAO: 'Negociacao',
  FECHADO: 'Fechado',
  PERDIDO: 'Perdido',
};

export default function CrmPage() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dashboard, setDashboard] = useState<DashboardData>({
    totalLeads: 0,
    conversionRate: 0,
    capturedThisMonth: 0,
    dealsClosedMonth: 0,
    ranking: [],
  });
  const [leads, setLeads] = useState<Lead[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [interactions, setInteractions] = useState<any[]>([]);

  const [leadForm, setLeadForm] = useState({
    name: '',
    phone: '',
    email: '',
    interestType: 'COMPRA',
    source: 'site',
    assignedBrokerId: '',
    desiredRegion: '',
    valueMin: '',
    valueMax: '',
    notes: '',
  });

  const [interactionForm, setInteractionForm] = useState({
    type: 'LIGACAO',
    notes: '',
  });

  const [appointmentForm, setAppointmentForm] = useState({
    scheduledAt: '',
    location: '',
    notes: '',
  });

  const [messageForm, setMessageForm] = useState({
    channel: 'EMAIL',
    recipient: '',
    subject: '',
    body: '',
  });

  const brokerOptions = useMemo(
    () => users.filter((user) => ['ADMIN', 'DIRETOR', 'GERENTE', 'CORRETOR'].includes(user.profile || '')),
    [users],
  );

  const loadDashboard = async () => {
    const response = await api.get('/crm/dashboard');
    setDashboard(response.data);
  };

  const loadLeads = async () => {
    const response = await api.get('/crm/leads?limit=100');
    setLeads(response.data?.data || []);
  };

  const loadUsers = async () => {
    const response = await api.get('/users?limit=200');
    const rows = response.data?.data || [];
    setUsers(rows);

    if (!leadForm.assignedBrokerId && rows.length > 0) {
      const defaultBroker = rows.find((item: UserOption) => ['ADMIN', 'DIRETOR', 'GERENTE', 'CORRETOR'].includes(item.profile || ''));
      if (defaultBroker) {
        setLeadForm((prev) => ({ ...prev, assignedBrokerId: defaultBroker.id }));
      }
    }
  };

  const loadAll = async () => {
    try {
      setLoading(true);
      await Promise.all([loadDashboard(), loadLeads(), loadUsers()]);
    } catch (error) {
      console.error('Erro ao carregar dados CRM:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLeadDetails = async (leadId: string) => {
    const [leadRes, interactionsRes] = await Promise.all([
      api.get(`/crm/leads/${leadId}`),
      api.get(`/crm/leads/${leadId}/interactions`),
    ]);

    const lead = leadRes.data;
    setSelectedLead(lead);
    setInteractions(interactionsRes.data || []);
    setMessageForm((prev) => ({
      ...prev,
      recipient: lead.email || lead.phone || '',
    }));
  };

  useEffect(() => {
    loadAll();
  }, []);

  const createLead = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await api.post('/crm/leads', {
        ...leadForm,
        valueMin: leadForm.valueMin ? Number(leadForm.valueMin) : undefined,
        valueMax: leadForm.valueMax ? Number(leadForm.valueMax) : undefined,
      });
      setLeadForm((prev) => ({
        ...prev,
        name: '',
        phone: '',
        email: '',
        desiredRegion: '',
        valueMin: '',
        valueMax: '',
        notes: '',
      }));
      await loadAll();
    } catch (error) {
      console.error('Erro ao criar lead:', error);
      alert('Nao foi possivel cadastrar o lead.');
    } finally {
      setSubmitting(false);
    }
  };

  const updateStage = async (leadId: string, stage: Lead['stage']) => {
    try {
      await api.patch(`/crm/leads/${leadId}/stage`, {
        stage,
        triggerAutomation: true,
      });
      await loadLeads();
      await loadDashboard();
      if (selectedLead?.id === leadId) {
        await loadLeadDetails(leadId);
      }
    } catch (error) {
      console.error('Erro ao atualizar etapa:', error);
      alert('Nao foi possivel atualizar a etapa.');
    }
  };

  const addInteraction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLead) return;

    try {
      setSubmitting(true);
      await api.post(`/crm/leads/${selectedLead.id}/interactions`, interactionForm);
      setInteractionForm({ type: 'LIGACAO', notes: '' });
      await loadLeadDetails(selectedLead.id);
    } catch (error) {
      console.error('Erro ao registrar interacao:', error);
      alert('Nao foi possivel registrar a interacao.');
    } finally {
      setSubmitting(false);
    }
  };

  const scheduleVisit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLead) return;

    try {
      setSubmitting(true);
      await api.post(`/crm/leads/${selectedLead.id}/appointments`, {
        ...appointmentForm,
        triggerAutomation: true,
      });
      setAppointmentForm({ scheduledAt: '', location: '', notes: '' });
      await loadLeadDetails(selectedLead.id);
      await loadLeads();
    } catch (error) {
      console.error('Erro ao agendar visita:', error);
      alert('Nao foi possivel agendar a visita.');
    } finally {
      setSubmitting(false);
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLead) return;

    try {
      setSubmitting(true);
      await api.post('/crm/messages/send', {
        ...messageForm,
        leadId: selectedLead.id,
      });
      setMessageForm((prev) => ({
        ...prev,
        subject: '',
        body: '',
      }));
      await loadLeadDetails(selectedLead.id);
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      alert('Nao foi possivel enviar a mensagem. Verifique configuracao SMTP/SMS.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Carregando modulo CRM...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">CRM Imobiliario + Email/SMS</h1>
        <p className="text-gray-600">Gestao de leads, funil, agenda e comunicacao automatica.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <div className="card flex items-center gap-3">
          <Users className="w-6 h-6 text-primary-600" />
          <div>
            <p className="text-xs text-gray-600">Total de leads</p>
            <p className="text-xl font-semibold">{dashboard.totalLeads}</p>
          </div>
        </div>
        <div className="card flex items-center gap-3">
          <TrendingUp className="w-6 h-6 text-emerald-600" />
          <div>
            <p className="text-xs text-gray-600">Taxa de conversao</p>
            <p className="text-xl font-semibold">{dashboard.conversionRate}%</p>
          </div>
        </div>
        <div className="card flex items-center gap-3">
          <BarChart3 className="w-6 h-6 text-indigo-600" />
          <div>
            <p className="text-xs text-gray-600">Captados no mes</p>
            <p className="text-xl font-semibold">{dashboard.capturedThisMonth}</p>
          </div>
        </div>
        <div className="card flex items-center gap-3">
          <CalendarClock className="w-6 h-6 text-orange-600" />
          <div>
            <p className="text-xs text-gray-600">Fechados no mes</p>
            <p className="text-xl font-semibold">{dashboard.dealsClosedMonth}</p>
          </div>
        </div>
        <div className="card">
          <p className="text-xs text-gray-600 mb-1">Top corretor</p>
          <p className="text-sm font-semibold">{dashboard.ranking?.[0]?.brokerName || 'Sem dados'}</p>
          <p className="text-xs text-gray-500">{dashboard.ranking?.[0]?.closedDeals || 0} fechamentos</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="card xl:col-span-1">
          <h2 className="text-lg font-semibold mb-3">Novo Lead</h2>
          <form className="space-y-3" onSubmit={createLead}>
            <div>
              <label className="label">Nome</label>
              <input className="input" value={leadForm.name} onChange={(e) => setLeadForm((p) => ({ ...p, name: e.target.value }))} required />
            </div>
            <div>
              <label className="label">Telefone</label>
              <input className="input" value={leadForm.phone} onChange={(e) => setLeadForm((p) => ({ ...p, phone: e.target.value }))} required />
            </div>
            <div>
              <label className="label">Email</label>
              <input className="input" type="email" value={leadForm.email} onChange={(e) => setLeadForm((p) => ({ ...p, email: e.target.value }))} />
            </div>
            <div>
              <label className="label">Interesse</label>
              <select className="input" value={leadForm.interestType} onChange={(e) => setLeadForm((p) => ({ ...p, interestType: e.target.value }))}>
                <option value="COMPRA">Compra</option>
                <option value="VENDA">Venda</option>
                <option value="ALUGUEL">Aluguel</option>
              </select>
            </div>
            <div>
              <label className="label">Origem do lead</label>
              <input className="input" value={leadForm.source} onChange={(e) => setLeadForm((p) => ({ ...p, source: e.target.value }))} required />
            </div>
            <div>
              <label className="label">Corretor responsavel</label>
              <select className="input" value={leadForm.assignedBrokerId} onChange={(e) => setLeadForm((p) => ({ ...p, assignedBrokerId: e.target.value }))} required>
                <option value="">Selecione...</option>
                {brokerOptions.map((item) => (
                  <option key={item.id} value={item.id}>{item.name} ({item.profile})</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">Faixa min.</label>
                <input className="input" type="number" min={0} value={leadForm.valueMin} onChange={(e) => setLeadForm((p) => ({ ...p, valueMin: e.target.value }))} />
              </div>
              <div>
                <label className="label">Faixa max.</label>
                <input className="input" type="number" min={0} value={leadForm.valueMax} onChange={(e) => setLeadForm((p) => ({ ...p, valueMax: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="label">Regiao desejada</label>
              <input className="input" value={leadForm.desiredRegion} onChange={(e) => setLeadForm((p) => ({ ...p, desiredRegion: e.target.value }))} />
            </div>
            <div>
              <label className="label">Observacoes</label>
              <textarea className="input min-h-[80px]" value={leadForm.notes} onChange={(e) => setLeadForm((p) => ({ ...p, notes: e.target.value }))} />
            </div>
            <button className="btn-primary w-full" disabled={submitting}>Cadastrar lead</button>
          </form>
        </div>

        <div className="card xl:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Funil de Leads</h2>
            <button className="btn-secondary" onClick={loadAll}>Atualizar</button>
          </div>

          <div className="overflow-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2">Lead</th>
                  <th>Interesse</th>
                  <th>Origem</th>
                  <th>Corretor</th>
                  <th>Etapa</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.id} className="border-b hover:bg-gray-50">
                    <td className="py-2">
                      <p className="font-medium">{lead.name}</p>
                      <p className="text-xs text-gray-500">{lead.phone} {lead.email ? `• ${lead.email}` : ''}</p>
                    </td>
                    <td>{lead.interestType}</td>
                    <td>{lead.source}</td>
                    <td>{lead.assignedBroker?.name || '-'}</td>
                    <td>
                      <select className="input min-w-[170px]" value={lead.stage} onChange={(e) => updateStage(lead.id, e.target.value as Lead['stage'])}>
                        {Object.entries(stageLabels).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button className="btn-secondary" onClick={() => loadLeadDetails(lead.id)}>Detalhes</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedLead && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="card">
            <h3 className="font-semibold mb-2">Lead selecionado</h3>
            <p className="text-sm font-medium">{selectedLead.name}</p>
            <p className="text-xs text-gray-600">{selectedLead.phone} {selectedLead.email ? `• ${selectedLead.email}` : ''}</p>
            <p className="text-xs text-gray-600 mt-1">Etapa: {stageLabels[selectedLead.stage]}</p>

            <form className="space-y-2 mt-4" onSubmit={addInteraction}>
              <h4 className="text-sm font-semibold">Nova interacao</h4>
              <select className="input" value={interactionForm.type} onChange={(e) => setInteractionForm((p) => ({ ...p, type: e.target.value }))}>
                <option value="LIGACAO">Ligacao</option>
                <option value="EMAIL">Email</option>
                <option value="SMS">SMS</option>
                <option value="WHATSAPP">WhatsApp</option>
                <option value="REUNIAO">Reuniao</option>
                <option value="OBSERVACAO">Observacao</option>
              </select>
              <textarea className="input min-h-[90px]" value={interactionForm.notes} onChange={(e) => setInteractionForm((p) => ({ ...p, notes: e.target.value }))} required />
              <button className="btn-primary w-full" disabled={submitting}>Registrar</button>
            </form>

            <form className="space-y-2 mt-5" onSubmit={scheduleVisit}>
              <h4 className="text-sm font-semibold">Agendar visita</h4>
              <input className="input" type="datetime-local" value={appointmentForm.scheduledAt} onChange={(e) => setAppointmentForm((p) => ({ ...p, scheduledAt: e.target.value }))} required />
              <input className="input" placeholder="Local" value={appointmentForm.location} onChange={(e) => setAppointmentForm((p) => ({ ...p, location: e.target.value }))} />
              <textarea className="input min-h-[70px]" placeholder="Observacoes" value={appointmentForm.notes} onChange={(e) => setAppointmentForm((p) => ({ ...p, notes: e.target.value }))} />
              <button className="btn-secondary w-full" disabled={submitting}>Agendar</button>
            </form>
          </div>

          <div className="card">
            <h3 className="font-semibold mb-3">Historico de interacoes</h3>
            <div className="space-y-2 max-h-[440px] overflow-auto">
              {interactions.map((item) => (
                <div key={item.id} className="rounded-lg border p-3 text-sm">
                  <p className="font-medium">{item.type}</p>
                  <p className="text-gray-700 mt-1">{item.notes}</p>
                  <p className="text-xs text-gray-500 mt-1">{new Date(item.occurredAt).toLocaleString('pt-BR')} • {item.user?.name}</p>
                </div>
              ))}
              {!interactions.length && (
                <p className="text-sm text-gray-500">Sem interacoes registradas.</p>
              )}
            </div>
          </div>

          <div className="card">
            <h3 className="font-semibold mb-3">Envio de Email/SMS</h3>
            <form className="space-y-3" onSubmit={sendMessage}>
              <div>
                <label className="label">Canal</label>
                <select className="input" value={messageForm.channel} onChange={(e) => setMessageForm((p) => ({ ...p, channel: e.target.value, recipient: e.target.value === 'EMAIL' ? selectedLead.email || '' : selectedLead.phone || '' }))}>
                  <option value="EMAIL">Email</option>
                  <option value="SMS">SMS</option>
                </select>
              </div>
              <div>
                <label className="label">Destinatario</label>
                <input className="input" value={messageForm.recipient} onChange={(e) => setMessageForm((p) => ({ ...p, recipient: e.target.value }))} required />
              </div>
              {messageForm.channel === 'EMAIL' && (
                <div>
                  <label className="label">Assunto</label>
                  <input className="input" value={messageForm.subject} onChange={(e) => setMessageForm((p) => ({ ...p, subject: e.target.value }))} required />
                </div>
              )}
              <div>
                <label className="label">Mensagem</label>
                <textarea className="input min-h-[120px]" value={messageForm.body} onChange={(e) => setMessageForm((p) => ({ ...p, body: e.target.value }))} required />
              </div>
              <button className="btn-primary w-full flex items-center justify-center gap-2" disabled={submitting}>
                <Send className="w-4 h-4" /> Enviar
              </button>
            </form>

            <div className="mt-4 rounded-lg bg-gray-50 p-3 text-xs text-gray-600 space-y-1">
              <p className="flex items-center gap-2"><Mail className="w-3 h-3" /> Email via SMTP ou SendGrid</p>
              <p className="flex items-center gap-2"><MessageSquare className="w-3 h-3" /> SMS via Twilio ou webhook</p>
              <p className="flex items-center gap-2"><Phone className="w-3 h-3" /> Automacoes por evento (lead/etapa/visita)</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
